import React, { useState, useMemo } from 'react';
import { merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import { isMobileOnly } from 'react-device-detect';

import { AppBar, Collapse, Box, Divider, useMediaQuery } from '@mui/material';

import { ArrowRightIcon, ArrowLeftIcon } from '@icons';

import { IMytaverseAppBar, AppBarPositionEnum } from './interfaces';

import HomeBarButton from '@core/dashboard/components/HomeBarButton';

import { getButtonPosition } from './helpers';
import { useStyles } from './styles';

const iconMap = {
  left: {
    open: <ArrowLeftIcon />,
    closed: <ArrowRightIcon />,
  },
  right: {
    open: <ArrowRightIcon />,
    closed: <ArrowLeftIcon />,
  },
};

const MytaverseAppBar = ({
  children,
  position = AppBarPositionEnum.TopLeft,
  isCollapsible = true,
  isGroup = true,
  sx,
  visiblePart,
  customOrderBarIsOpen,
  setCustomOrderBarIsOpen,
}: IMytaverseAppBar) => {
  const [isOpen, setIsOpen] = useState(true);

  const { t: translate } = useTranslation();

  const isMobile = useMediaQuery('(max-width: 961px)');

  const classes = useStyles();
  const rootSx = useMemo(
    () =>
      merge(
        isGroup ? classes.root : classes.rootSecondary,
        classes[position],
        classes.section,
        isCollapsible ? classes.sectionSmall : {},
        sx,
      ),
    [isCollapsible, position],
  );

  if (!isCollapsible) {
    return (
      <AppBar position="fixed" color="transparent" sx={rootSx}>
        {visiblePart ? visiblePart : null}
        {children}
      </AppBar>
    );
  }

  const buttonPosition = getButtonPosition(position);

  const collapseStyles =
    buttonPosition === 'right' ? { transform: 'scaleX(-1)' } : {};
  const boxStyles = {
    ...classes.section,
    ...(buttonPosition === 'right'
      ? { justifyContent: 'flex-end', transform: 'scaleX(-1)' }
      : {}),
  };
  const buttonStyles = buttonPosition === 'right' ? { order: -10 } : {};
  const dividerStyles = {
    ...classes.divider,
    ...(buttonPosition === 'right' ? { order: -2 } : {}),
  };
  const visiblePartStyles = {
    ...classes.section,
    paddingRight: 1,
    paddingLeft: 1,
    ...(buttonPosition === 'right' ? { order: -1 } : {}),
  };

  const isOpenValue = setCustomOrderBarIsOpen ? customOrderBarIsOpen : isOpen;

  return (
    <AppBar position="fixed" color="transparent" sx={rootSx}>
      <Collapse
        orientation="horizontal"
        in={isOpenValue}
        collapsedSize={isMobile ? 72 : 100}
        sx={collapseStyles}
      >
        <Box sx={boxStyles}>
          {children}
          {!isMobileOnly ? (
            <Divider orientation="vertical" flexItem sx={dividerStyles} />
          ) : null}
        </Box>
      </Collapse>
      {visiblePart ? <Box sx={visiblePartStyles}>{visiblePart}</Box> : null}
      {isMobileOnly ? (
        <Divider orientation="vertical" flexItem sx={dividerStyles} />
      ) : null}
      <HomeBarButton
        label={translate(isOpenValue ? 'tooltips.collapse' : 'tooltips.expand')}
        icon={iconMap[buttonPosition][isOpenValue ? 'open' : 'closed']}
        handleClick={() => {
          if (setCustomOrderBarIsOpen) {
            setCustomOrderBarIsOpen((prevState) => !prevState);
          } else {
            setIsOpen((prevState) => !prevState);
          }
        }}
        sx={buttonStyles}
      />
    </AppBar>
  );
};

export default MytaverseAppBar;
